/**
 * Ежедневный бонус и серия входов (7-дневный цикл). Дата — местная (YYYY-MM-DD).
 * Пропустили день — серия начинается заново. В «Награды ×2» бонус удваивается.
 */
import type { GameState } from './types';
import type { PackGrants, PackResult } from './grants';
import { applyGrants } from './grants';
import { addPassBonus, eventMult } from './season';

export const DAILY_CYCLE = 7;
/** Одежда, которую можно получить на 7-й день (берётся первая, которой у игрока ещё нет). */
const MYSTERY_CLOTHING = ['cl_hair_wave_silver', 'cl_face_cat', 'cl_acc_glasses', 'cl_top_hawaii', 'cl_bottom_grass', 'cl_hat_flower', 'cl_acc_shell', 'cl_hat_shark'];

export const dateKey = (now: number): string => new Date(now).toLocaleDateString('sv');
function yesterdayKey(now: number): string {
  const d = new Date(now); d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('sv');
}

export interface DailyStatus {
  canClaim: boolean;
  /** Серия подряд (с учётом того, что вчера заходили). Если сегодня уже забрали — текущая серия. */
  streak: number;
  /** Какой день цикла (1…7) будет получен сегодня, а если уже получен — сегодняшний. */
  day: number;
}

export function dailyStatus(state: GameState, now: number): DailyStatus {
  const lg = state.login ?? { last: '', streak: 0 };
  const today = dateKey(now);
  if (lg.last === today) return { canClaim: false, streak: lg.streak, day: ((lg.streak - 1) % DAILY_CYCLE) + 1 };
  const streak = lg.last === yesterdayKey(now) ? lg.streak + 1 : 1;
  return { canClaim: true, streak, day: ((streak - 1) % DAILY_CYCLE) + 1 };
}

/** Что даётся в день `day` цикла. Награда растёт вместе с уровнем игрока. */
export function dailyReward(state: GameState, day: number, mult = 1): PackGrants {
  const s = (1 + state.player.level * 0.08) * mult;
  const r = (n: number): number => Math.round((n * s) / 10) * 10;
  switch (day) {
    case 1: return { coins: r(1_500) };
    case 2: return { food: r(150) };
    case 3: return { coins: r(3_000), fullEnergy: true };
    case 4: return { coins: r(5_000) };
    case 5: return { xp: r(200), coins: r(1_000) };
    case 6: return { coins: r(4_000), food: r(400) };
    default: {
      const cl = MYSTERY_CLOTHING.find((id) => !state.player.wardrobe.includes(id));
      return { coins: r(12_000), ...(cl ? { clothing: [cl] } : { coins: r(20_000) }) };
    }
  }
}

export function claimDaily(state: GameState, now: number, petName: (id: string) => string): { ok: boolean; day: number; streak: number; grants: PackGrants; res: PackResult | null } {
  const st = dailyStatus(state, now);
  if (!st.canClaim) return { ok: false, day: st.day, streak: st.streak, grants: {}, res: null };
  const mult = eventMult('daily');
  const grants = dailyReward(state, st.day, mult);
  const res = applyGrants(state, grants, now, petName);
  state.login = { last: dateKey(now), streak: st.streak };
  addPassBonus(state, Math.round(10 * mult), now);
  return { ok: true, day: st.day, streak: st.streak, grants, res };
}
